import { useGetFaqByMultipleSlugsQuery } from 'store/queries/cms-api'
import { useStoreSelector } from 'hooks/store.ts'
import { useGetLanguage } from 'hooks/use-get-language'
import { FaqSlugB2B, FaqSlugB2C } from 'types/faq'
import SearchBar from 'components/search/SearchBar.tsx'
import styles from './Faq.module.scss'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import { Heading, Icon } from '@boltenergy-be/design-system'
import parse, { Element } from 'html-react-parser'
import Link from 'components/link/Link.tsx'
import { routes } from 'types/routes.ts'
import FaqDrawer from 'components/faq-drawer/FaqDrawer.tsx'
import { useEffect, useEffectEvent, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { selectIsProducerAccount } from 'store/contact/slice.ts'
import { FaqCategory } from 'types/cms.ts'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'
import { UrlSearchParamsKeys } from 'store/app/types.ts'

const FAQ_CATEGORIES_B2C = Object.values(FaqSlugB2C)
const FAQ_CATEGORIES_B2B = Object.values(FaqSlugB2B)
const FAQS_TO_HIDE = [FaqSlugB2C.MARKET_PRICES, FaqSlugB2C.BOLT_FIX_OPT_IN] as (FaqSlugB2C | FaqSlugB2B)[]

const Faq = () => {
  // Redux store
  const { urlSearchParams } = useStoreSelector((store) => store.app)
  const isProducer = useStoreSelector(selectIsProducerAccount)

  // i18n
  const { t } = useTranslation(['common', 'support'])
  const language = useGetLanguage()

  // Access rights
  const { todayTomorrow } = useGetAccessRight(ACCESS_RIGHT_KEYS.marketPrices)

  // Queries
  const { data, isLoading } = useGetFaqByMultipleSlugsQuery({
    slugs: (isProducer ? FAQ_CATEGORIES_B2B : FAQ_CATEGORIES_B2C).filter((slug) =>
      !todayTomorrow.canAccess ? !FAQS_TO_HIDE.includes(slug) : slug
    ),
    language
  })

  // Local state
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [detailModalOpen, setDetailModalOpen] = useState<boolean>(false)
  const [selectedCategory, setSelectedCategory] = useState<FaqCategory | null>(null)

  // useEffect Events
  const setSelectedCategoryEvent = useEffectEvent((category: FaqCategory | null) => {
    setSelectedCategory(category)
  })
  const setDetailModalOpenEvent = useEffectEvent((open: boolean) => {
    setDetailModalOpen(open)
  })

  // Constants
  const filteredCategories = useMemo<FaqCategory[]>(() => {
    const categories = data?.categories || []
    return searchQuery ? categories?.filter((cat) => cat.name.toLowerCase().includes(searchQuery.toLowerCase())) : categories
  }, [data?.categories, searchQuery])

  useEffect(() => {
    if (urlSearchParams?.[UrlSearchParamsKeys.FAQ_CATEGORY]) {
      const categoryFromUrl = data?.categories.find((cat) => cat.slug === urlSearchParams[UrlSearchParamsKeys.FAQ_CATEGORY])

      if (categoryFromUrl) {
        setSelectedCategoryEvent(categoryFromUrl)
        setDetailModalOpenEvent(true)
      }
    }
  }, [data?.categories, urlSearchParams])

  return (
    <section>
      <SearchBar className={styles.search} onInput={(query) => setSearchQuery(query)} placeholder={t(($) => $.faq.searchPlaceholder)} />

      {isLoading ? (
        <LoadingSkeleton>
          {[...Array(5)].map((_, i) => (
            <LoadingSkeleton.Rectangle height={50} key={i} />
          ))}
        </LoadingSkeleton>
      ) : (
        <ul className={styles['faq-section']}>
          {filteredCategories.length > 0 ? (
            filteredCategories.map((cat) =>
              cat.questions.length > 0 ? (
                <li key={cat.id}>
                  <button
                    onClick={() => {
                      setSelectedCategory(cat)
                      setDetailModalOpen(true)
                    }}
                  >
                    <Heading as="h2" variant="h6" className="my-200">
                      {cat.name}
                    </Heading>

                    <Icon name="chevronRight" />
                  </button>
                </li>
              ) : null
            )
          ) : (
            <li className={styles['no-results-container']}>
              {parse(
                t(($) => $.faq.noResults),
                {
                  replace: (domNode) => {
                    if (domNode instanceof Element && domNode.attribs && domNode.attribs.id === 'icon') {
                      return <Icon name="externalLink" />
                    }

                    return domNode
                  }
                }
              )}
            </li>
          )}
        </ul>
      )}

      <footer className={styles['faq-footer']}>
        <small>
          {t(($) => $.footer.noAnswer, {
            ns: 'support'
          })}
        </small>
        <Link href={routes.SUPPORT_CONTACT} variant="secondary" size="small">
          {t(($) => $.contact.tab, {
            ns: 'support'
          })}
        </Link>
      </footer>

      {selectedCategory && (
        <FaqDrawer
          isOpen={detailModalOpen}
          onRequestClose={() => setDetailModalOpen(false)}
          title={selectedCategory.name}
          questions={selectedCategory.questions}
        />
      )}
    </section>
  )
}

export default Faq
